/**
 * HelpQuickActions - Deep links from help articles (#1359)
 *
 * Renders action buttons at the bottom of help articles that navigate
 * directly to the described feature. Club-scoped paths resolve {slug}
 * via useClubNavContext.
 */

import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useClubNavContext } from '../../hooks/useClubNavContext';
import type { HelpQuickAction } from '../../types/help';

interface HelpQuickActionsProps {
  actions: HelpQuickAction[];
}

export default function HelpQuickActions({ actions }: HelpQuickActionsProps) {
  const navigate = useNavigate();
  const clubSlug = useClubNavContext();

  const handleAction = (action: HelpQuickAction) => {
    if (action.requiresClub) {
      if (!clubSlug) {
        // No club context — send to clubs directory
        navigate('/clubs');
        return;
      }
      navigate(action.path.replace('{slug}', clubSlug));
    } else {
      navigate(action.path);
    }
  };

  return (
    <div className="mt-8">
      <h3
        className="text-xs font-semibold uppercase tracking-wider mb-3"
        style={{ color: 'var(--text-muted)' }}
      >
        Quick Actions
      </h3>
      <div className="space-y-2">
        {actions.map((action) => (
          <button
            key={action.path}
            type="button"
            onClick={() => handleAction(action)}
            className="w-full flex items-center justify-between rounded-lg border p-3 transition-all hover:border-[var(--oro-gold)]"
            style={{
              backgroundColor: 'var(--oro-surface)',
              borderColor: 'var(--oro-border)',
            }}
          >
            <span
              className="text-sm font-medium"
              style={{ color: 'var(--text-primary)' }}
            >
              {action.label}
            </span>
            <ChevronRight
              className="h-4 w-4 shrink-0"
              style={{ color: 'var(--oro-gold)' }}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
